import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import Button from '@ui-elements/Button';
import ProgressTracker from '@ui-parts/ProgressTracker';

import { ROUTES } from '~/constants/routes';
import { USER_CATEGORY } from '~/common/constants';
import { PROGRESS_TRACKER_LABELS, PROGRESS_TYPE, USER_REGISTRATION_PROGRESSES } from '~/constants';
import { PALETTE, FONT_SIZES, TYPOGRAPHY } from '~/assets/styles/base';
import { registerUser } from '~/apiTs';
import { validationInitialized } from '~/features/validation/validationSlice';
import { GMOASK_STATUS } from '~/constants/api';

const StyledBox = styled.div`
  background: ${PALETTE.white};
  width: 720px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 20px auto 55px;
  padding: 55px 50px;
  a {
    text-decoration: none;
  }
`;

const InputWrapper = styled.div`
  width: 620px;
  text-align: left;
  border-bottom: 1px solid ${PALETTE.border.frame};
  margin: 20px 0;
  font-size: ${TYPOGRAPHY.body3};
`;

const InputTitle = styled.div`
  font-size: ${FONT_SIZES.body3};
  margin: 10px 0;
`;

const InputText = styled.div`
  font-size: ${FONT_SIZES.bodyDefault};
  margin: 10px 0;
  word-break: break-word;
`;
const InputTextP = styled.input`
  font-size: ${FONT_SIZES.bodyDefault};
  margin: 10px 0;
  background: ${PALETTE.white};
  border: none;
`;
const FlexWrap = styled.div`
  display: flex;
  padding: 0 100px;
  justify-content: space-evenly;
`;

const Title = styled(Typography)`
  text-align: center;
  font-size: ${TYPOGRAPHY.title4};
  color: ${PALETTE.primary.main};
  margin-bottom: 30px;
`;

const StyledAlert = styled.div`
  border: 1px solid ${PALETTE.error.main};
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 12px 16px;
  margin-top: -10px;
  margin-bottom: 26px;
  position: relative;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  svg {
    font-size: 1.8rem;
    margin-right: 2px;
  }
`;

// ユーザ仮登録確認画面
function SignupConfirm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const validation = useSelector((state) => state.validation);
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    // signupページを介さずに直接confirmページのURL入力した場合は登録画面に飛ばす
    if (user.name === '') {
      navigate(ROUTES.USER.SIGNUP);
    }
    dispatch(validationInitialized('signup'));
  }, []);

  const handleClickSubmit = async () => {
    const res = await dispatch(
      registerUser({
        name: user.name,
        email: user.email,
        password: user.password,
        category: user.category,
        individualCategory: user.individualCategory,
        companyName: user.companyName,
        departmentName: user.departmentName,
        phone: user.phone,
        zip: user.zip,
        pref: user.pref,
        address: user.address,
      })
    );
    if (res.payload.status === GMOASK_STATUS.OK) {
      navigate(ROUTES.USER.SIGNUP_COMPLETED);
    }
  };

  useEffect(() => {
    if (validation.signup.errors.invalid) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [validation.signup.errors.invalid]);

  return (
    <>
      <ProgressTracker
        width="618px"
        labels={PROGRESS_TRACKER_LABELS[PROGRESS_TYPE.USER_REGISTRATION].map((label) => t(label))}
        currentNum={USER_REGISTRATION_PROGRESSES[ROUTES.USER.SIGNUP_CONFIRM]}
      />

      <StyledBox>
        <Title>{t('registration.proceedNext')}</Title>
        {validation.signup.errors.invalid && (
          <StyledAlert severity="error" color="error">
            <StyledTypography variant="body3">
              <WarningRoundedIcon />
              <span data-testid="User-Signup-Response-Error-Message">
                {validation.signup.errors.message}
              </span>
            </StyledTypography>
          </StyledAlert>
        )}
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-email">
            {t('registration.email')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-email">{user.email}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-password">
            {t('registration.password')}
          </InputTitle>
          <InputTextP
            data-testid="pages-SignupConfirm-input-password"
            type="password"
            value={user.password}
            disabled
          />
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-name">
            {t('registration.name')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-name">{user.name}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-category">
            {t('registration.category')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-category">
            {t(`registration.user.individualCategory.${user.individualCategory}`)}
          </InputText>
        </InputWrapper>
        {user.category === USER_CATEGORY.CORPORATE ? (
          <>
            <InputWrapper>
              <InputTitle data-testid="pages-SignupConfirm-label-company">
                {t('registration.company')}
              </InputTitle>
              <InputText data-testid="pages-SignupConfirm-input-company">
                {user.companyName}
              </InputText>
            </InputWrapper>
            <InputWrapper>
              <InputTitle data-testid="pages-SignupConfirm-label-department">
                {t('registration.department')}
              </InputTitle>
              <InputText data-testid="pages-SignupConfirm-input-department">
                {user.departmentName}
              </InputText>
            </InputWrapper>
          </>
        ) : null}
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-zip">
            {t('registration.zip')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-zip">{user.zip}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-prefecture">
            {t('registration.prefecture')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-prefecture">{user.pref}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-address">
            {t('registration.address')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-address">{user.address}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-phone">
            {t('registration.phone')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-phone">{user.phone}</InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-privacy">
            {t('registration.privacyPolicy')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-privacy">
            {t('registration.agreeYes')}
          </InputText>
        </InputWrapper>
        <InputWrapper>
          <InputTitle data-testid="pages-SignupConfirm-label-gmosign">
            {t('registration.gmoSign')}
          </InputTitle>
          <InputText data-testid="pages-SignupConfirm-input-gmosign">
            {t('registration.agreeYes')}
          </InputText>
        </InputWrapper>
        <FlexWrap>
          <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
            <Button
              variant="outlined"
              color="secondary"
              colorType="gray"
              handleClick={() => {
                navigate(ROUTES.USER.SIGNUP, { state });
              }}
              data-testid="pages-SignupConfirm-backbtn"
              sx={{ width: 148 }}
            >
              {t('appCommon.back')}
            </Button>
          </Box>
          <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
            <Button
              variant="contained"
              data-testid="pages-SignupConfirm-submitbtn"
              onClick={handleClickSubmit}
              sx={{ width: 148 }}
            >
              {t('appCommon.submit')}
            </Button>
          </Box>
        </FlexWrap>
      </StyledBox>
    </>
  );
}

export default SignupConfirm;
